import Web3 from 'web3'
import { Address, Wei } from '../../utils/types'
import { Pool as PoolAbi } from '../../abi/abi'
import { maxDepositSlippage, whiteListedDepositToken } from '../../config'
import { ceilWei, floorWei } from '../../utils/static-web3'
import { PoolWithData, TokenWithData } from '../api-types'
import {
  DepositStates,
  ViewState,
} from '../../components/pools/deposit/DepositState'
import { TokenApi } from './token-api'
import { extractErrorMessage } from '../../logic/errors/extract-error-message'
import { Token } from '../../logic/token/token'
import { Hash } from '../../logic/transactions/transactions'
import { Pool } from '../../logic/pools/Pool'
import { Amounts } from '../../logic/token/amounts'

export function getOneAssetJoinTokens(pool: Pool): Token[] {
  const poolTokenSymbols = pool.tokens.map(t => t.symbol)
  const others = whiteListedDepositToken.filter(
    t => !poolTokenSymbols.includes(t.symbol)
  )
  return [...pool.tokens, ...others]
}

// There is no max management by design
/**
 * @deprecated : use Proxy
 * @param web3
 * @param account
 * @param pool
 * @param amounts
 * @param setState
 */
export async function deposit(
  web3: Web3,
  account: Address,
  pool: PoolWithData,
  amounts: Map<TokenWithData, Wei>,
  setState: (state: ViewState) => void
): Promise<DepositResult | undefined> {
  const {
    CONNECT,
    CHOOSE_AMOUNT,
    ALLOWANCE_LOADING,
    PROCESSING,
    PREVIEW,
    VALID,
    TRANSACTION_SUCCESS,
    ALLOWANCE_FAILED,
    TRANSACTION_FAILED,
  } = DepositStates
  // @ts-ignore
  var poolContract = new web3.eth.Contract(PoolAbi, pool['address'])

  const tokens = pool.tokens

  const leadToken = tokens[0]
  const leadAmount = amounts.get(leadToken)!
  const leadBalance = leadToken.balance

  // We are forced to make a lowered approximation here
  const poolValueRatio = (Number(leadAmount) / Number(leadBalance)) * 0.99999

  // get tokens information
  const finalTokens = pool.tokens
  const numTokens = finalTokens.length
  let maxAmountIn = new Array(numTokens)
  setState(ALLOWANCE_LOADING.withSubState({ accepted: 0 }))
  try {
    for (let i = 0; i < numTokens; i++) {
      // balance du Pool, pas du user
      const token = tokens[i]

      const tokenBalance = token.balance

      // this is all calculated in wei
      const exactShare = Number(tokenBalance) * poolValueRatio
      const maxShare = ceilWei(exactShare * (1 + maxDepositSlippage))
      maxAmountIn[i] = maxShare

      const tokenApi = new TokenApi(web3, account, token)
      await tokenApi.allow(pool.address, maxShare)
      setState(ALLOWANCE_LOADING.withSubState({ accepted: i + 1 }))
    }
  } catch (error) {
    setState(ALLOWANCE_FAILED.withError(extractErrorMessage(error)))
    console.error(error)
    return
  }

  // join
  const totalSupply = Number(pool.totalShares)
  const targetInShareWei = floorWei(poolValueRatio * totalSupply)

  setState(PROCESSING)
  try {
    const gasEstimate = await poolContract.methods
      .joinPool(targetInShareWei, maxAmountIn)
      .estimateGas({ from: account })
    const result = await poolContract.methods
      .joinPool(targetInShareWei, maxAmountIn)
      .send({
        from: account,
        gas: gasEstimate,
        maxPriorityFeePerGas: null,
        maxFeePerGas: null,
      })

    const data = result.events.Transfer[4].returnValues

    const { symbol, name, address } = await TokenApi.getTokenInfo(
      web3,
      data.from
    )

    const deposit: DepositResult = {
      address,
      name: name || 'SWAAP-LP-' + tokens.map(t => t.name).join('-'),
      symbol: symbol || 'SwLP-' + tokens.map(t => t.symbol).join('-'),
      value: data.value,
      amounts,
      pool,
      hash: 'Not supported, use Proxy',
      bringIn: Amounts.fromMap(amounts),
      mode: '1-asset',
    }

    // TODO: The postview don't reflect the exact swapped amounts
    setState(TRANSACTION_SUCCESS.withSubState({ deposit }))
    return deposit
  } catch (error: any) {
    console.error(error)
    let message = error.message || error
    setState(TRANSACTION_FAILED.withSubState({ error: true, message }))
  }
}

export interface DepositResult {
  pool: PoolWithData
  address: Address
  name: string
  symbol: string
  value: Wei
  amounts: Map<Token, Wei>
  bringIn: Amounts
  mode: 'all-asset' | '1-asset' | 'aggregator'
  hash: Hash
}
